import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../../app/components/ui/button";
import { Checkbox } from "../../../app/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../app/components/ui/dialog";
import { Input } from "../../../app/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";
import type { Product } from "../hooks/useProducts";

export function BarcodePrintModal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: Product[];
  preselectedIds?: string[];
}) {
  const { open, onOpenChange, products, preselectedIds } = props;
  const { token } = useAuth();

  const [selected, setSelected] = useState<Set<string>>(() => new Set(preselectedIds ?? []));
  const [copies, setCopies] = useState(1);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set((preselectedIds ?? []).map(String)));
  }, [open, preselectedIds]);

  const rows = useMemo(() => {
    return products.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const selectedCount = selected.size;

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const onPrint = async () => {
    if (!token) return;
    if (working) return;
    if (selectedCount === 0) {
      toast.error("Select at least one product");
      return;
    }
    const nCopies = Number(copies);
    if (!Number.isFinite(nCopies) || nCopies <= 0) {
      toast.error("Enter a valid copies count");
      return;
    }
    setWorking(true);
    try {
      const blob = await api.products.printBarcodes({
        token,
        product_ids: Array.from(selected),
        copies: nCopies,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("PDF generated");
      onOpenChange(false);
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to generate PDF");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Print barcode labels</DialogTitle>
          <DialogDescription>Select products and number of copies.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">{selectedCount} selected</div>
          <div className="flex items-center gap-2">
            <div className="text-sm">Copies</div>
            <Input
              className="w-24"
              type="number"
              min={1}
              max={50}
              value={String(copies)}
              onChange={(e) => setCopies(Number(e.target.value))}
            />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Barcode</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id}>
                <TableCell>
                  <Checkbox checked={selected.has(String(p.id))} onCheckedChange={(v) => toggle(String(p.id), v === true)} />
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.sku ?? "-"}</TableCell>
                <TableCell>{p.barcode ? String(p.barcode) : <span className="text-muted-foreground">Missing</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={working}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void onPrint()} disabled={working}>
            {working ? "Generating..." : "Print"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

